import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import RefreshIcon from "@mui/icons-material/Refresh";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import { getMaterialsUsageStatistics } from "../../../../services/Admin/ConversationsApi";
import { useAuth } from "../../../../Context/AuthContext";


const toNum = (value) => {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
};

const formatAmount = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return "0";
	return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const StatisticsMaterialUsagePage = () => {
	const [materialsUsage, setMaterialsUsage] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const { hasPermission } = useAuth();

	const fetchMaterialsUsage = async (filters = {}) => {
		setLoading(true);
		setError("");

		try {
			const response = await getMaterialsUsageStatistics(filters);
			setMaterialsUsage(Array.isArray(response?.data) ? response.data : []);
		} catch (err) {
			console.error("خطأ في جلب إحصائيات استهلاك الخامات:", err);
			setError(err?.message || "حدث خطأ أثناء جلب بيانات استهلاك الخامات");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchMaterialsUsage();
	}, []);

	const handleApplyDateFilter = () => {
		if (fromDate && toDate && fromDate > toDate) {
			setError("تاريخ من يجب أن يكون أقل من أو يساوي تاريخ إلى");
			return;
		}

		fetchMaterialsUsage({
			...(fromDate ? { from_date: fromDate } : {}),
			...(toDate ? { to_date: toDate } : {}),
		});
	};

	const totals = useMemo(() => {
		const materialsCount = materialsUsage.length;
		const totalUsedAllMaterials = materialsUsage.reduce((sum, item) => sum + toNum(item?.total_used), 0);
		const totalDrinkUsages = materialsUsage.reduce(
			(sum, item) => sum + (Array.isArray(item?.drinks) ? item.drinks.length : 0),
			0
		);

		return {
			materialsCount,
			totalUsedAllMaterials,
			totalDrinkUsages,
		};
	}, [materialsUsage]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 360,
				}}
			>
				<CircularProgress size={56} />
			</Box>
		);
	}

	// Excel export handler (must be inside the component, but outside return)
	const handleExportExcel = () => {
		const exportData = materialsUsage.flatMap((material) => {
			const drinks = Array.isArray(material.drinks) ? material.drinks : [];
			if (drinks.length === 0) {
				return [{
					"معرف الخامة": material.material_id,
					"اسم الخامة": material.material_name,
					"الوحدة": material.unit,
					"الإجمالي المستخدم": formatAmount(material.total_used),
					"عدد المشروبات": 0,
					"معرف المشروب": "-",
					"اسم المشروب": "-",
					"الكمية المستخدمة": "-"
				}];
			}
			return drinks.map((drink) => ({
				"معرف الخامة": material.material_id,
				"اسم الخامة": material.material_name,
				"الوحدة": material.unit,
				"الإجمالي المستخدم": formatAmount(material.total_used),
				"عدد المشروبات": drinks.length,
				"معرف المشروب": drink.drink_id,
				"اسم المشروب": drink.drink_name,
				"الكمية المستخدمة": `${formatAmount(drink.amount_used)} ${material.unit || ''}`
			}));
		});
		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "مواد مستهلكة");
		XLSX.writeFile(workbook, `materials_usage_export_${new Date().toISOString().slice(0,10)}.xlsx`);
	};

	return (
		<Box dir="rtl" sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<ScienceIcon sx={{ fontSize: 36, color: "primary.main" }} />
					<Typography variant="h4" sx={{ fontWeight: "bold" }}>
						إحصائيات استهلاك الخامات
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchMaterialsUsage}>
						تحديث البيانات
					</Button>
					              {hasPermission("export") && (

					<Button
						variant="contained"
						color="success"
						onClick={handleExportExcel}
						disabled={materialsUsage.length === 0}
					>
						تصدير إلى Excel
					</Button>
					)}
				</Box>
			</Box>



			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
					gap: 1.5,
					mb: 2,
					alignItems: "end",
				}}
			>
				<TextField
					label="من تاريخ"
					type="date"
					value={fromDate}
					onChange={(e) => setFromDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					fullWidth
				/>
				<TextField
					label="إلى تاريخ"
					type="date"
					value={toDate}
					onChange={(e) => setToDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					fullWidth
				/>
				<Button variant="contained" onClick={handleApplyDateFilter}>
					تطبيق التاريخ
				</Button>
			</Box>

			{(fromDate || toDate) && (
				<Alert severity="info" sx={{ mb: 2 }}>
					الفترة الحالية: {fromDate || "-"} إلى {toDate || "-"}
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{error}
				</Alert>
			)}

			{materialsUsage.length > 0 && (
				<Grid container spacing={2} sx={{ mb: 3 }}>
					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #5b86e5 0%, #36d1dc 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									عدد الخامات
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{totals.materialsCount}
								</Typography>
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									إجمالي الكمية المستخدمة
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{formatAmount(totals.totalUsedAllMaterials)}
								</Typography>
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									مرات الاستخدام داخل المشروبات
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{totals.totalDrinkUsages}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			)}

			{materialsUsage.length === 0 ? (
				<Paper sx={{ p: 5, textAlign: "center" }}>
					<Inventory2Icon sx={{ fontSize: 72, color: "text.secondary", mb: 1 }} />
					<Typography variant="h6" color="text.secondary">
						لا توجد بيانات استهلاك خامات حالياً
					</Typography>
				</Paper>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{materialsUsage.map((material, index) => {
						const drinks = Array.isArray(material?.drinks) ? material.drinks : [];

						return (
							<Paper key={material?.material_id || index} sx={{ p: 2 }}>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Inventory2Icon color="primary" />
										<Typography variant="h6" sx={{ fontWeight: 800 }}>
											{material?.material_name || "-"}
										</Typography>
									</Box>

									<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
										<Chip label={`ID: ${material?.material_id ?? "-"}`} size="small" variant="outlined" />
										<Chip label={`الإجمالي المستخدم: ${formatAmount(material?.total_used)} ${material?.unit || ""}`} size="small" color="primary" />
										<Chip label={`عدد المشروبات: ${drinks.length}`} size="small" color="secondary" variant="outlined" />
									</Box>
								</Box>

								<TableContainer component={Paper} variant="outlined">
									<Table size="small" dir="rtl">
										<TableHead>
											<TableRow sx={{ backgroundColor: "grey.100" }}>
												<TableCell sx={{ fontWeight: 700, textAlign: "right" }}>#</TableCell>
												<TableCell sx={{ fontWeight: 700, textAlign: "right" }}>رقم المشروب</TableCell>
												<TableCell sx={{ fontWeight: 700, textAlign: "right" }}>اسم المشروب</TableCell>
												<TableCell sx={{ fontWeight: 700, textAlign: "right" }}>الكمية المستخدمة</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{drinks.length === 0 ? (
												<TableRow>
													<TableCell colSpan={4} sx={{ textAlign: "center", py: 2 }}>
														<Typography variant="body2" color="text.secondary">
															لا توجد مشروبات مرتبطة بهذه الخامة
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												drinks.map((drink, idx) => (
													<TableRow key={`${material?.material_id || "mat"}-${drink?.drink_id || idx}`}>
														<TableCell sx={{ textAlign: "right" }}>{idx + 1}</TableCell>
														<TableCell sx={{ textAlign: "right" }}>{drink?.drink_id ?? "-"}</TableCell>
														<TableCell sx={{ textAlign: "right" }}>
															<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
																<LocalCafeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
																<Typography variant="body2">{drink?.drink_name || "-"}</Typography>
															</Box>
														</TableCell>
														<TableCell sx={{ textAlign: "right", fontWeight: 700 }}>
															{formatAmount(drink?.amount_used)} {material?.unit || ""}
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Paper>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export default StatisticsMaterialUsagePage;
